import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import {
  VictoryArea,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryLabel,
  VictoryScatter,
  VictoryVoronoiContainer,
} from "victory";
import { Ionicons } from "@expo/vector-icons";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useScreenDimensions, useDevice } from "../../_layout";
import FloatingMenu from "../../Components/FloatingMenu";
import Tooltip from "../../Components/Tooltip";
import {
  METRIC_OPTIONS,
  METRIC_TOOLTIPS,
  coerceMetricKey,
  defaultMetricByType,
  filterMetricOptions,
  getMetricValue,
} from "./statisticsMetrics";

const CARD_PADDING = 16;
const CHART_PADDING = { top: 40, bottom: 40, left: 0, right: 0 };
const DOMAIN_PADDING_X = { left: 16, right: 16 };
const MIN_LABEL_GAP_PX = 26;

const formatAxisLabel = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  if (!isFinite(n)) return "";
  const threshold = 0.05;
  return Math.abs(n % 1) < threshold ? n.toFixed(0) : n.toFixed(1);
};

const getTimeDomainFromModelSeriesPoints = (modelSeries) => {
  let minMs = null;
  let maxMs = null;

  (modelSeries ?? []).forEach((s) => {
    const points = Array.isArray(s?.points) ? s.points : [];
    points.forEach((p) => {
      const raw = p?.dateMs ?? p?.x ?? null;
      const ms = typeof raw === "number" ? raw : Number(raw);
      if (!isFinite(ms)) return;

      // In weeks mode, x can be a small week index (1..N). We only want
      // true timestamps here.
      if (ms <= 0) return;
      if (ms < 1000 * 60 * 60 * 24 * 365) return;

      minMs = minMs == null ? ms : Math.min(minMs, ms);
      maxMs = maxMs == null ? ms : Math.max(maxMs, ms);
    });
  });

  if (minMs == null || maxMs == null) return null;

  // Give the chart a little breathing room left/right.
  const padMs = 24 * 60 * 60 * 1000;
  return {
    start: new Date(minMs - padMs),
    end: new Date(maxMs + padMs),
  };
};

// New Statistics chart (v2 store-driven).
// Intentionally no tooltip + no legend yet.

const ChartTypePicker = observer(
  ({ chartType, metricByType, setMetricByType, t, metricOptions }) => {
    const [isHovered, setIsHovered] = useState(false);
    const iconColor = Colors.grayColor;
    const { isMobile } = useDevice();

    const icon =
      chartType === "line" ? (
        <Ionicons
          name="analytics-sharp"
          size={16}
          color={iconColor}
          style={{ marginRight: 6 }}
        />
      ) : chartType === "bar" ? (
        <Ionicons
          name="stats-chart"
          size={16}
          color={iconColor}
          style={{ marginRight: 6 }}
        />
      ) : (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            width: 16,
            marginRight: 6,
            justifyContent: "space-between",
          }}
        >
          <View style={{ width: 6, height: 2, backgroundColor: iconColor }} />
          <View style={{ width: 6, height: 2, backgroundColor: iconColor }} />
        </View>
      );

    const currentMetricKey = metricByType?.[chartType] ?? "e1rm";
    const selectedMetric =
      (metricOptions ?? []).find((m) => m.key === currentMetricKey) ?? null;
    const currentMetricLabel = selectedMetric?.labelKey
      ? t(selectedMetric.labelKey)
      : selectedMetric?.label ?? t("statisticsMetricNone-string");

    return (
      <FloatingMenu
        placement="bottom-start"
        width={230}
        maxHeight={280}
        trigger={
          <View
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={[
              GlobalStyles.HStack,
              {
                height: 30,
                paddingHorizontal: 8,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: isHovered ? Colors.grayColor : Colors.borderColor,
                backgroundColor: isHovered
                  ? Colors.hoveredGray
                  : Colors.whiteColor,
                alignItems: "center",
                cursor: "pointer",
              },
            ]}
          >
            {icon}
            <Text
              style={[
                Fonts.regular14,
                { color: Colors.blackColor, fontSize: 13, flexShrink: 1 },
              ]}
              numberOfLines={1}
            >
              {currentMetricLabel}
            </Text>
            <Ionicons
              name="chevron-down"
              size={14}
              color={Colors.grayColor}
              style={{ marginLeft: 6 }}
            />
          </View>
        }
        items={metricOptions}
        renderItem={({ item, close }) => {
          const tooltipKey = METRIC_TOOLTIPS[item.key];
          return (
            <Tooltip
              content={tooltipKey}
              placement="right"
              delayedOpen
              disabled={!tooltipKey}
            >
              <TouchableOpacity
                onPress={() => {
                  setMetricByType((prev) => ({
                    ...prev,
                    [chartType]: item.key,
                  }));
                  close();
                }}
                style={{ paddingVertical: 10, paddingHorizontal: 12 }}
                activeOpacity={0.8}
              >
                <Text
                  style={[
                    Fonts.regular14,
                    { color: Colors.blackColor, fontSize: 13 },
                  ]}
                  numberOfLines={1}
                >
                  {item.labelKey ? t(item.labelKey) : item.label}
                </Text>
              </TouchableOpacity>
            </Tooltip>
          );
        }}
      />
    );
  }
);

const ClientStatisticsChart = observer(({ store }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const enableTooltipInteractions = !isMobile;

  const allowStressIndex = !store?.client?.coach?.rirOrRpe;

  const [chartDimensions, setChartDimensions] = useState({
    width: 320,
    height: 360,
  });

  const metricByType =
    store?.resolvedMetricByType ?? defaultMetricByType(allowStressIndex);

  const setMetricByType = (updater) => {
    if (!store?.setMetricByType) return;
    store.setMetricByType(updater);
  };

  const [tooltipState, setTooltipState] = useState({
    xKey: null,
    xLabel: "",
    rows: [],
  });

  const onChartLayout = (event) => {
    const { width, height } = event?.nativeEvent?.layout ?? {};
    if (typeof width === "number" && typeof height === "number") {
      // RN-web can report 0 width inside certain horizontal ScrollView layouts.
      // VictoryChart with width=0 renders nothing.
      if (width <= 0 || height <= 0) return;
      setChartDimensions({ width, height });
    }
  };

  if (!store) return null;

  const model = store?.getChartSeriesModel
    ? store.getChartSeriesModel({ t, language: i18n.language })
    : { mode: store?.mode, xAxisType: "time", series: [] };

  const series = model?.series ?? [];
  const mode = model?.mode ?? store?.mode;

  const metricOptions = filterMetricOptions(allowStressIndex, METRIC_OPTIONS);

  const isMultiBlockWeeksCompare =
    mode === "weeks" && (store?.selectedBlockIds?.length ?? 0) > 1;

  // Legacy condition: only allow bar chart in "single series" scenarios.
  const canShowBar =
    (store?.selectedBlockIds?.length ?? 0) <= 1 &&
    (store?.tracks?.length ?? 0) <= 1 &&
    series.length === 1;

  const hasNoSeries = !series || series.length === 0;
  const canShowBarResolved = hasNoSeries ? true : canShowBar;

  const buildSampleSeries = () => {
    const sampleColor = Colors.bubbleColor;

    // A tiny deterministic wobble so the sample doesn't look like a perfect trend line.
    const wobble = (i, amp = 1, freq = 1) => Math.sin(i * freq) * amp;

    if (mode === "weeks") {
      const weekStartRaw = Number(store?.startWeek ?? 1) || 1;
      const weekEndRaw = Number(store?.endWeek ?? weekStartRaw) || weekStartRaw;
      const safeWeekStart = Math.max(1, Math.min(weekStartRaw, weekEndRaw));
      const safeWeekEnd = Math.max(safeWeekStart, weekEndRaw);

      const totalWeeks = Math.max(1, safeWeekEnd - safeWeekStart + 1);
      const pointCount = Math.min(12, totalWeeks);
      const step =
        totalWeeks <= pointCount
          ? 1
          : Math.max(1, Math.floor(totalWeeks / pointCount));

      const points = Array.from({ length: pointCount }, (_, i) => {
        const week = safeWeekStart + i * step;
        const base = 140 + i * 2.2;
        return {
          week,
          e1rm: Math.max(50, base + wobble(i, 4, 0.9) - wobble(i, 2, 0.35)),
          stressIndex: Math.max(5, 32 + wobble(i, 10, 0.7) + wobble(i, 4, 1.8)),
          tonnage: Math.max(1000, 8500 + i * 520 + wobble(i, 1200, 0.55)),
          averageIntensity: Math.max(
            20,
            65 + wobble(i, 6, 0.8) - wobble(i, 3, 0.4)
          ),
          numberOfLifts: Math.max(
            5,
            18 + i * 2 + Math.floor(wobble(i, 2, 0.6))
          ),
          numberOfSets: Math.max(1, 3 + (i % 4)),
          exertionLoad: Math.max(50, 420 + i * 28 + wobble(i, 80, 0.5)),
          color: sampleColor,
        };
      }).filter((p) => p.week <= safeWeekEnd);

      return [
        {
          seriesKey: "sample",
          label: "",
          exerciseDisplayName: "",
          protocolSlot: null,
          points,
          color: sampleColor,
        },
      ];
    }

    const endMsRaw =
      store?.endDate instanceof Date ? store.endDate.getTime() : Date.now();
    const startMsRaw =
      store?.startDate instanceof Date ? store.startDate.getTime() : 0;

    const isAllTime = startMsRaw === 0;
    const endMs = isFinite(endMsRaw) ? endMsRaw : Date.now();
    const startMs = isAllTime ? endMs - 14 * 24 * 60 * 60 * 1000 : startMsRaw;

    const spanMs = Math.max(1, endMs - startMs);
    const pointCount = 10;
    const stepMs = Math.max(1, Math.floor(spanMs / (pointCount - 1)));

    const points = Array.from({ length: pointCount }, (_, i) => {
      const dateMs = startMs + i * stepMs;
      const base = 140 + i * 1.9;
      return {
        dateMs,
        e1rm: Math.max(50, base + wobble(i, 5, 0.85) - wobble(i, 2, 0.3)),
        stressIndex: Math.max(5, 32 + wobble(i, 11, 0.65) + wobble(i, 5, 1.7)),
        tonnage: Math.max(1000, 8500 + i * 480 + wobble(i, 1400, 0.5)),
        averageIntensity: Math.max(
          20,
          68 + wobble(i, 7, 0.7) - wobble(i, 3, 0.35)
        ),
        numberOfLifts: Math.max(5, 16 + i * 2 + Math.floor(wobble(i, 2, 0.55))),
        numberOfSets: Math.max(1, 3 + (i % 3)),
        exertionLoad: Math.max(50, 390 + i * 30 + wobble(i, 90, 0.6)),
        color: sampleColor,
      };
    });

    return [
      {
        seriesKey: "sample",
        label: "",
        exerciseDisplayName: "",
        protocolSlot: null,
        points,
        color: sampleColor,
      },
    ];
  };

  const xTickFormat = (x) => {
    if (mode === "weeks") {
      const n = typeof x === "number" ? x : Number(x);
      if (!isFinite(n)) return "";
      return t("wOfExercise-string", { week: n });
    }

    const d = x instanceof Date ? x : new Date(Number(x));
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "2-digit",
    });
  };

  // When comparing across multiple blocks (weeks mode), the table uses
  // `getColorForBlock(blockId)` as the primary color identity.
  const buildMultiBlockWeeksSeries = () => {
    const track = (store?.tracks ?? [])?.[0] ?? null;
    if (!track?.id || !store?.getTrackPoints) return [];

    const allPoints = store.getTrackPoints(track.id, {
      t,
      language: i18n.language,
    });

    const selectedIds = (store?.selectedBlockIds ?? []).filter(Boolean);
    const slotMode = track?.slotMode ?? "any";

    return selectedIds.flatMap((blockId) => {
      const pointsForBlock = (allPoints ?? []).filter(
        (p) => (p?.blockId ?? null) === blockId
      );

      const blockName = store?.getBlockById
        ? store.getBlockById(blockId)?.name ?? String(blockId)
        : String(blockId);

      const blockColor = store?.getColorForBlock
        ? store.getColorForBlock(blockId)
        : Colors.bubbleColor;

      // Match table behavior: if slotMode=any and multiple slots exist within
      // this block, split into (block, slot) series.
      if (slotMode !== "exact") {
        const slotKeys = Array.from(
          new Set(
            (pointsForBlock ?? [])
              .map((p) => p?.protocolSlot ?? null)
              .filter((s) => s != null)
          )
        ).sort((a, b) => String(a).localeCompare(String(b)));

        if (slotKeys.length > 1) {
          return slotKeys.map((slotKey) => {
            const slotPoints = (pointsForBlock ?? []).filter(
              (p) => (p?.protocolSlot ?? null) === slotKey
            );

            // Use slot-variant colors so split series stay distinct across blocks.
            const seriesColor = store?.getColorForExerciseAndSlot
              ? store.getColorForExerciseAndSlot(
                  track?.exerciseDisplayName ?? "",
                  slotKey,
                  { forceSlotVariant: true, identitySalt: blockId }
                )
              : null;

            const slotPointsWithColor = slotPoints.map((p) => ({
              ...p,
              color: seriesColor ?? blockColor ?? p?.color,
            }));

            return {
              seriesKey: `${String(blockId)}::${String(slotKey ?? "none")}`,
              label: blockName,
              points: slotPointsWithColor,
              __blockId: blockId,
              __blockName: blockName,
              __blockColor: blockColor,
              __protocolSlot: slotKey,
              __exerciseDisplayName: track?.exerciseDisplayName ?? "",
              __seriesColor: seriesColor,
              __isSlotSplitWithinBlock: true,
            };
          });
        }
      }

      const seriesColor = blockColor;

      const blockPointsWithColor = (pointsForBlock ?? []).map((p) => ({
        ...p,
        color: seriesColor ?? p?.color,
      }));

      return [
        {
          seriesKey: String(blockId),
          label: blockName,
          points: blockPointsWithColor,
          __blockId: blockId,
          __blockName: blockName,
          __blockColor: blockColor,
          __protocolSlot:
            slotMode === "exact" ? track?.protocolSlot ?? null : null,
          __exerciseDisplayName: track?.exerciseDisplayName ?? "",
          __seriesColor: seriesColor,
          __isSlotSplitWithinBlock: false,
        },
      ];
    });
  };

  const chartSeries = hasNoSeries
    ? buildSampleSeries()
    : isMultiBlockWeeksCompare
    ? buildMultiBlockWeeksSeries()
    : series;

  // Legacy behavior: line + dashed/area + bar coexist at the same time.
  // Each picker selects which metric that *layer* displays.
  const getSeriesColor = (s) => {
    if (s?.color) return s.color;

    // Multi-block compare: chart identity should match the table's block color.
    if (isMultiBlockWeeksCompare) {
      // If we split one block into multiple series (one per slot), those
      // series must be distinguishable. In that case, use the same per-slot
      // series color that the table model computes.
      if (s?.__isSlotSplitWithinBlock && s?.__seriesColor) {
        return s.__seriesColor;
      }
      return s?.__blockColor ?? Colors.bubbleColor;
    }

    // Time mode / single-block weeks: match the table/store exercise+slot color.
    const exerciseDisplayName = s?.exerciseDisplayName ?? "";
    const protocolSlot = s?.protocolSlot ?? null;

    if (store?.getColorForExerciseAndSlot && exerciseDisplayName) {
      try {
        return store.getColorForExerciseAndSlot(
          exerciseDisplayName,
          protocolSlot
        );
      } catch (e) {
        // fall through
      }
    }

    const firstPoint = Array.isArray(s?.points) ? s.points[0] : null;
    return firstPoint?.color ?? s?.color ?? Colors.bubbleColor;
  };

  const buildChartDataBySeries = (metricKey) => {
    if (!metricKey || metricKey === "none") return [];

    return chartSeries.map((s) => {
      const points = Array.isArray(s?.points) ? s.points : [];
      const data = points
        .map((p) => {
          if (!p) return null;
          const x =
            mode === "weeks"
              ? (() => {
                  const raw = p?.week ?? p?.x ?? null;
                  const n = typeof raw === "number" ? raw : Number(raw);
                  return isFinite(n) ? n : null;
                })()
              : p?.dateMs != null
              ? new Date(Number(p.dateMs))
              : p?.x != null
              ? new Date(Number(p.x))
              : null;

          if (x == null) return null;
          if (mode !== "weeks" && x instanceof Date && isNaN(x.getTime())) {
            return null;
          }

          return {
            x,
            y: getMetricValue(p, metricKey),
          };
        })
        .filter((d) => {
          if (!d) return false;
          if (d.y == null) return false;
          const y = typeof d.y === "number" ? d.y : Number(d.y);
          return isFinite(y);
        });

      const color = getSeriesColor(s);
      const key = s?.seriesKey ?? s?.label ?? "series";

      return {
        key,
        color,
        label: s?.label ?? key,
        exerciseDisplayName: s?.exerciseDisplayName ?? s?.__exerciseDisplayName,
        protocolSlot: s?.protocolSlot ?? s?.__protocolSlot,
        blockName: s?.__blockName ?? null,
        data,
        rawPoints: points,
      };
    });
  };

  const getMaxYAcrossSeries = (seriesData) => {
    let maxY = 0;
    (seriesData ?? []).forEach((sd) => {
      (sd?.data ?? []).forEach((d) => {
        const y = typeof d?.y === "number" ? d.y : Number(d?.y);
        if (isFinite(y)) maxY = Math.max(maxY, y);
      });
    });
    return maxY;
  };

  const lineMetricKey = coerceMetricKey(
    metricByType?.line,
    "e1rm",
    metricOptions
  );
  const areaMetricKey = coerceMetricKey(
    metricByType?.area,
    allowStressIndex ? "stressIndex" : "intensityPercentage",
    metricOptions
  );
  const barMetricKey = coerceMetricKey(
    metricByType?.bar,
    "tonnage",
    metricOptions
  );

  const lineSeriesData = buildChartDataBySeries(lineMetricKey);
  const areaSeriesData = buildChartDataBySeries(areaMetricKey);
  const barSeriesData = canShowBarResolved
    ? buildChartDataBySeries(barMetricKey)
    : [];

  const maxLine = getMaxYAcrossSeries(lineSeriesData);
  const maxArea = getMaxYAcrossSeries(areaSeriesData);
  const maxBar = getMaxYAcrossSeries(barSeriesData);

  const globalMax = Math.max(
    lineMetricKey === "none" ? 0 : maxLine,
    areaMetricKey === "none" ? 0 : maxArea,
    !canShowBarResolved || barMetricKey === "none" ? 0 : maxBar
  );

  const safeRatio = (layerMax) => {
    if (!globalMax || !isFinite(globalMax)) return 1;
    if (!layerMax || !isFinite(layerMax)) return 1;
    if (layerMax === 0) return 1;
    return globalMax / layerMax;
  };

  const ratioLine = safeRatio(maxLine);
  const ratioArea = safeRatio(maxArea);
  const ratioBar = safeRatio(maxBar);

  const metricOptionMap = useMemo(() => {
    const map = new Map();
    (metricOptions ?? []).forEach((m) => map.set(m.key, m));
    return map;
  }, [metricOptions]);

  const getMetricLabel = (metricKey) => {
    const opt = metricOptionMap.get(metricKey);
    if (!opt) return String(metricKey ?? "");
    return opt.labelKey ? t(opt.labelKey) : opt.label ?? String(metricKey);
  };

  const formatMetricValueForTooltip = (point, metricKey) => {
    if (!point || !metricKey || metricKey === "none") return "-";
    const rawValue = getMetricValue(point, metricKey);
    const valueNumber =
      typeof rawValue === "number" ? rawValue : Number(rawValue);
    if (!isFinite(valueNumber)) return "-";
    if (valueNumber > 0) {
      const formatted = store?._formatLabel?.(valueNumber);
      return formatted ?? formatAxisLabel(valueNumber);
    }
    const hasDuplicates = (point?.duplicateCountInX ?? 0) > 1;
    return hasDuplicates ? "0" : "-";
  };

  const formatLinePointValue = (value) => {
    const n = typeof value === "number" ? value : Number(value);
    if (!isFinite(n)) return "";
    const formatted = store?._formatLabel?.(n);
    return formatted ?? formatAxisLabel(n);
  };

  const getXKeyFromPoint = (p) => {
    if (!p) return null;
    const raw =
      mode === "weeks" ? p?.week ?? p?.x ?? null : p?.dateMs ?? p?.x ?? null;
    const n = typeof raw === "number" ? raw : Number(raw);
    return isFinite(n) ? n : null;
  };

  const getXKeyFromDatum = (datum) => {
    if (!datum) return null;
    const candidate = datum.x ?? datum._x ?? null;
    const raw = candidate instanceof Date ? candidate.getTime() : candidate;
    const n = typeof raw === "number" ? raw : Number(raw);
    return isFinite(n) ? n : null;
  };

  const xValues = useMemo(() => {
    const set = new Set();
    (chartSeries ?? []).forEach((s) => {
      (s?.points ?? []).forEach((p) => {
        const x = getXKeyFromPoint(p);
        if (x != null && isFinite(x)) set.add(x);
      });
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [chartSeries, mode]);

  const findNearestXKey = (x) => {
    if (!xValues || xValues.length === 0) return null;
    let nearest = xValues[0];
    let bestDist = Math.abs(x - nearest);
    for (let i = 1; i < xValues.length; i++) {
      const v = xValues[i];
      const dist = Math.abs(x - v);
      if (dist < bestDist) {
        bestDist = dist;
        nearest = v;
      }
    }
    return nearest;
  };

  const buildSeriesLabelForTooltip = (series, point) => {
    const baseName = String(
      series?.exerciseDisplayName ?? series?.label ?? ""
    ).trim();

    if (isMultiBlockWeeksCompare) {
      const blockName = String(
        series?.blockName ?? series?.__blockName ?? baseName
      ).trim();
      return blockName || t("exerciseHeader-string");
    }

    return baseName || t("exerciseHeader-string");
  };

  const activeLayers = [
    lineMetricKey !== "none"
      ? {
          type: "line",
          metricKey: lineMetricKey,
          label: getMetricLabel(lineMetricKey),
        }
      : null,
    areaMetricKey !== "none"
      ? {
          type: "area",
          metricKey: areaMetricKey,
          label: getMetricLabel(areaMetricKey),
        }
      : null,
    canShowBarResolved && barMetricKey !== "none"
      ? {
          type: "bar",
          metricKey: barMetricKey,
          label: getMetricLabel(barMetricKey),
        }
      : null,
  ].filter(Boolean);

  const getXPixelForXValue = (xKey) => {
    if (xKey == null) return null;

    const paddingLeft = CHART_PADDING.left + DOMAIN_PADDING_X.left;
    const paddingRight = CHART_PADDING.right + DOMAIN_PADDING_X.right;

    const widthForMath = Math.max(
      1,
      Number(chartDimensions.width ?? resolvedWidth ?? 0)
    );

    const plotWidth = Math.max(1, widthForMath - paddingLeft - paddingRight);

    const rawStart =
      mode === "weeks"
        ? safeWeekStart
        : xDomainStart instanceof Date
        ? xDomainStart.getTime()
        : Number(xDomainStart);
    const rawEnd =
      mode === "weeks"
        ? safeWeekEnd
        : xDomainEnd instanceof Date
        ? xDomainEnd.getTime()
        : Number(xDomainEnd);

    if (!isFinite(rawStart) || !isFinite(rawEnd) || rawEnd === rawStart)
      return null;

    const clampedX = Math.min(Math.max(xKey, rawStart), rawEnd);
    const ratio = (clampedX - rawStart) / (rawEnd - rawStart);
    return paddingLeft + ratio * plotWidth;
  };

  const buildTooltipState = (xKey) => {
    if (xKey == null || hasNoSeries) {
      return { xKey: null, xLabel: "", rows: [], xPixel: null };
    }

    const xLabel =
      mode === "weeks"
        ? t("wOfExercise-string", { week: xKey })
        : new Date(xKey).toLocaleDateString();

    const rows = [];

    chartSeries.forEach((series) => {
      const points = Array.isArray(series?.points) ? series.points : [];
      points.forEach((p, idx) => {
        const pointX = getXKeyFromPoint(p);
        if (pointX == null || pointX !== xKey) return;

        const metrics = activeLayers.map((layer) => ({
          key: `${layer.type}-${layer.metricKey}`,
          layer: layer.type,
          label: layer.label,
          value: formatMetricValueForTooltip(p, layer.metricKey),
        }));

        rows.push({
          key: `${series?.seriesKey ?? series?.label ?? "series"}-${idx}`,
          color: getSeriesColor(series),
          label: buildSeriesLabelForTooltip(series, p),
          metrics,
          sets: p?.seriesText ?? "",
        });
      });
    });

    const xPixel = getXPixelForXValue(xKey);

    return { xKey, xLabel, rows, xPixel };
  };

  const handleVoronoiActivated = (points) => {
    if (!points || points.length === 0) return;
    const xKey = getXKeyFromDatum(points[0]);
    if (xKey == null) {
      setTooltipState({ xKey: null, xLabel: "", rows: [] });
      return;
    }
    setTooltipState(buildTooltipState(xKey));
  };

  const handleVoronoiDeactivated = () => {
    setTooltipState({ xKey: null, xLabel: "", rows: [] });
  };

  const handleDatumHover = (datum) => {
    const xKey = getXKeyFromDatum(datum);
    if (xKey == null) return;
    setTooltipState(buildTooltipState(xKey));
  };

  const getDomainXFromMousePx = (mouseX) => {
    if (typeof mouseX !== "number" || !isFinite(mouseX)) return null;

    const paddingLeft = CHART_PADDING.left + DOMAIN_PADDING_X.left;
    const paddingRight = CHART_PADDING.right + DOMAIN_PADDING_X.right;

    const widthForMath = Math.max(
      1,
      Number(chartDimensions.width ?? resolvedWidth ?? 0)
    );

    const plotWidth = Math.max(1, widthForMath - paddingLeft - paddingRight);

    const rawStart =
      mode === "weeks"
        ? safeWeekStart
        : xDomainStart instanceof Date
        ? xDomainStart.getTime()
        : Number(xDomainStart);
    const rawEnd =
      mode === "weeks"
        ? safeWeekEnd
        : xDomainEnd instanceof Date
        ? xDomainEnd.getTime()
        : Number(xDomainEnd);

    if (!isFinite(rawStart) || !isFinite(rawEnd) || rawEnd === rawStart)
      return null;

    const clampedX = Math.min(Math.max(mouseX - paddingLeft, 0), plotWidth);
    const ratio = clampedX / plotWidth;
    return rawStart + ratio * (rawEnd - rawStart);
  };

  const handleMouseMove = (event) => {
    const mouseX =
      event?.nativeEvent?.locationX ?? event?.nativeEvent?.offsetX ?? null;
    if (mouseX == null) return;
    const domainX = getDomainXFromMousePx(mouseX);
    if (domainX == null) return;
    const nearestX = findNearestXKey(domainX);
    if (nearestX == null) return;
    setTooltipState(buildTooltipState(nearestX));
  };

  const handleMouseLeave = () => {
    handleVoronoiDeactivated();
  };

  const [tooltipWidth, setTooltipWidth] = useState(null);

  const renderLineLayer = () => {
    if (lineMetricKey === "none") return null;

    const minPointGapPx = getMinPointPixelGap();
    const showLinePointLabels =
      lineSeriesData.length === 1 &&
      !hasNoSeries &&
      screenSize !== "small" &&
      minPointGapPx >= MIN_LABEL_GAP_PX;

    return lineSeriesData.map((sd) => (
      <VictoryGroup key={`line-${sd.key}`}>
        <VictoryLine
          data={sd.data}
          interpolation="monotoneX"
          style={{ data: { stroke: sd.color, strokeWidth: 2 } }}
          y={(datum) =>
            (typeof datum?.y === "number" ? datum.y : 0) * ratioLine
          }
          events={[
            {
              target: "data",
              eventHandlers: {
                onMouseMove: enableTooltipInteractions
                  ? (_evt, props) => {
                      handleDatumHover(props?.datum);
                      return null;
                    }
                  : undefined,
                onMouseLeave: enableTooltipInteractions
                  ? () => {
                      handleVoronoiDeactivated();
                      return null;
                    }
                  : undefined,
              },
            },
          ]}
        />
        <VictoryScatter
          data={sd.data}
          size={4}
          labels={
            showLinePointLabels
              ? ({ datum }) => formatLinePointValue(datum?.y)
              : undefined
          }
          labelComponent={
            showLinePointLabels ? (
              <VictoryLabel
                dy={-8}
                style={{
                  fill: Colors.darkgrayColor,
                  fontSize: 10,
                  fontWeight: "600",
                  fontFamily:
                    "system-ui, -apple-system, 'Segoe UI', sans-serif",
                }}
              />
            ) : undefined
          }
          style={{
            data: {
              fill: sd.color,
              stroke: Colors.whiteColor,
              strokeWidth: 2,
            },
          }}
          y={(datum) =>
            (typeof datum?.y === "number" ? datum.y : 0) * ratioLine
          }
          events={[
            {
              target: "data",
              eventHandlers: {
                onMouseMove: enableTooltipInteractions
                  ? (_evt, props) => {
                      handleDatumHover(props?.datum);
                      return null;
                    }
                  : undefined,
                onMouseLeave: enableTooltipInteractions
                  ? () => {
                      handleVoronoiDeactivated();
                      return null;
                    }
                  : undefined,
              },
            },
          ]}
        />
      </VictoryGroup>
    ));
  };

  const renderAreaLayer = () => {
    if (areaMetricKey === "none") return null;

    return areaSeriesData.map((sd) => (
      <VictoryGroup key={`area-${sd.key}`}>
        <VictoryArea
          data={sd.data}
          interpolation="monotoneX"
          style={{
            data: {
              fill: sd.color,
              opacity: 0.12,
            },
          }}
          y={(datum) =>
            (typeof datum?.y === "number" ? datum.y : 0) * ratioArea
          }
          events={[
            {
              target: "data",
              eventHandlers: {
                onMouseMove: (_evt, props) => {
                  handleDatumHover(props?.datum);
                  return null;
                },
                onMouseLeave: () => {
                  handleVoronoiDeactivated();
                  return null;
                },
              },
            },
          ]}
        />
        <VictoryLine
          data={sd.data}
          interpolation="monotoneX"
          style={{
            data: {
              stroke: sd.color,
              strokeWidth: 2,
              opacity: 0.6,
              strokeDasharray: [8, 8],
            },
          }}
          y={(datum) =>
            (typeof datum?.y === "number" ? datum.y : 0) * ratioArea
          }
          events={[
            {
              target: "data",
              eventHandlers: {
                onMouseMove: (_evt, props) => {
                  handleDatumHover(props?.datum);
                  return null;
                },
                onMouseLeave: () => {
                  handleVoronoiDeactivated();
                  return null;
                },
              },
            },
          ]}
        />
      </VictoryGroup>
    ));
  };

  const renderBarLayer = () => {
    if (!canShowBarResolved) return null;
    if (barMetricKey === "none") return null;

    // Legacy: bar only makes sense in a single-series scenario.
    const sd = barSeriesData?.[0];
    if (!sd) return null;

    const barCount = Math.max(1, (sd.data ?? []).length);

    // Keep bars from touching by sizing them relative to available pixel width.
    // Formula:
    //   slotWidth = plotWidth / barCount
    //   barWidth = clamp(4, slotWidth - gapPx, maxBarWidth)
    // where maxBarWidth matches legacy `ClientStatistics.js`.
    const maxBarWidth = screenSize === "small" ? 14 : 26;
    const gapPx = 6;

    const chartPaddingX = CHART_PADDING.left + CHART_PADDING.right;
    const domainPaddingX = DOMAIN_PADDING_X.left + DOMAIN_PADDING_X.right;
    const plotWidth = Math.max(
      1,
      resolvedWidth - chartPaddingX - domainPaddingX
    );
    const slotWidth = plotWidth / barCount;
    const computedBarWidth = Math.min(
      maxBarWidth,
      Math.max(4, Math.floor(slotWidth - gapPx))
    );

    const minGapPx = (() => {
      const xs = (sd.data ?? [])
        .map((d) => getXKeyFromDatum(d))
        .filter((v) => typeof v === "number" && isFinite(v))
        .map((x) => getXPixelForXValue(x))
        .filter((v) => typeof v === "number" && isFinite(v))
        .sort((a, b) => a - b);

      if (xs.length < 2) return Infinity;
      let gap = Infinity;
      for (let i = 1; i < xs.length; i++) {
        gap = Math.min(gap, xs[i] - xs[i - 1]);
      }
      return gap;
    })();

    const barWidth = (() => {
      // Use actual point spacing when available; otherwise fall back to uniform slot spacing.
      const spacingPx =
        isFinite(minGapPx) && minGapPx > 0 ? minGapPx : slotWidth;

      // Simple, collision-safe width: cap to a fraction of spacing so bars can't touch.
      const widthFromSpacing = Math.max(4, Math.floor(spacingPx * 0.6));
      const buffer = Math.max(2, Math.floor(gapPx * 0.75));
      const widthFromGap = Math.max(4, Math.floor(spacingPx - buffer));

      return Math.max(
        4,
        Math.min(widthFromSpacing, widthFromGap, computedBarWidth, maxBarWidth)
      );
    })();

    const cornerRadius = Math.max(0, Math.min(3, Math.floor(barWidth / 4)));

    return (
      <VictoryBar
        key={`bar-${sd.key}`}
        data={sd.data}
        barWidth={barWidth}
        cornerRadius={{ top: cornerRadius }}
        style={{ data: { fill: sd.color, opacity: 0.6 } }}
        y={(datum) => (typeof datum?.y === "number" ? datum.y : 0) * ratioBar}
        events={[
          {
            target: "data",
            eventHandlers: {
              onMouseMove: enableTooltipInteractions
                ? (_evt, props) => {
                    handleDatumHover(props?.datum);
                    return null;
                  }
                : undefined,
              onMouseLeave: enableTooltipInteractions
                ? () => {
                    handleVoronoiDeactivated();
                    return null;
                  }
                : undefined,
            },
          },
        ]}
      />
    );
  };

  const resolvedWidth = Math.max(320, Number(chartDimensions.width ?? 0) || 0);
  const resolvedHeight = Math.max(
    360,
    Number(chartDimensions.height ?? 0) || 0
  );

  useEffect(() => {
    if (hasNoSeries) {
      setTooltipState({ xKey: null, xLabel: "", rows: [] });
    }
  }, [hasNoSeries]);

  function getMinPointPixelGap() {
    if (!xValues || xValues.length < 2) return Infinity;

    const pixels = xValues
      .map((x) => getXPixelForXValue(x))
      .filter((v) => typeof v === "number" && isFinite(v))
      .sort((a, b) => a - b);

    if (pixels.length < 2) return Infinity;

    let minGap = Infinity;
    for (let i = 1; i < pixels.length; i++) {
      minGap = Math.min(minGap, pixels[i] - pixels[i - 1]);
    }
    return minGap;
  }

  const weekStart = Number(store?.startWeek ?? 1) || 1;
  const weekEnd = Number(store?.endWeek ?? weekStart) || weekStart;
  const safeWeekStart = Math.max(1, Math.min(weekStart, weekEnd));
  const safeWeekEnd = Math.max(safeWeekStart, weekEnd);

  const xDomainStartRaw =
    store?.startDate instanceof Date ? store.startDate : null;
  const xDomainEndRaw = store?.endDate instanceof Date ? store.endDate : null;
  const isAllTimeRange = (xDomainStartRaw?.getTime?.() ?? null) === 0;

  // In time-mode "all time", we still want a tight domain around the
  // compared exercise occurrences (first -> last), not an epoch->now domain.
  const timeDomainFromData =
    mode === "weeks" ? null : getTimeDomainFromModelSeriesPoints(chartSeries);

  const xDomainStart =
    mode === "weeks"
      ? null
      : isAllTimeRange && timeDomainFromData
      ? timeDomainFromData.start
      : xDomainStartRaw ?? new Date(0);

  const xDomainEnd =
    mode === "weeks"
      ? null
      : isAllTimeRange && timeDomainFromData
      ? timeDomainFromData.end
      : xDomainEndRaw ?? new Date();

  const xDomain =
    mode === "weeks"
      ? { x: [safeWeekStart, safeWeekEnd] }
      : {
          x: [xDomainStart, xDomainEnd],
        };

  const weekTickValues =
    mode === "weeks" && safeWeekEnd - safeWeekStart <= 30
      ? Array.from(
          { length: safeWeekEnd - safeWeekStart + 1 },
          (_, i) => safeWeekStart + i
        )
      : undefined;

  const chartContainer =
    hasNoSeries || !enableTooltipInteractions ? undefined : (
      <VictoryVoronoiContainer
        voronoiDimension="x"
        activateData
        voronoiPadding={20}
        activateLabels={false}
        labels={() => ""}
        onActivated={(points) => handleVoronoiActivated(points)}
        onDeactivated={handleVoronoiDeactivated}
      />
    );

  const Legend = () => {
    return (
      <View
        style={{
          flexWrap: "wrap",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingRight: CARD_PADDING,
          marginTop: 8,
        }}
      >
        {chartSeries.map((s) => {
          const isBlockSeries = isMultiBlockWeeksCompare;
          const baseName = isBlockSeries
            ? String(s?.__blockName ?? s?.label ?? "").trim()
            : String(s?.exerciseDisplayName ?? "").trim();

          const protocolSlot = isBlockSeries
            ? s?.__protocolSlot ?? null
            : s?.protocolSlot ?? null;

          const slotLabel = protocolSlot ? String(t(protocolSlot)) : "";

          const color = getSeriesColor(s);
          const key = s?.seriesKey ?? baseName;
          if (!baseName) return null;
          return (
            <View
              key={key}
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginRight: 15,
                marginBottom: 6,
              }}
            >
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 7.5,
                  marginRight: 5,
                  backgroundColor: color,
                }}
              />
              <Text
                style={[Fonts.regular12, { fontSize: 11.5 }]}
                numberOfLines={1}
              >
                <Text style={{ color: Colors.blackColor }}>{baseName}</Text>
                {slotLabel ? (
                  <Text
                    style={{ color: Colors.grayColor }}
                  >{` \u00A0\u00B7\u00A0${slotLabel}`}</Text>
                ) : null}
              </Text>
            </View>
          );
        })}
      </View>
    );
  };

  const TooltipOverlay = () => {
    if (!tooltipState?.rows || tooltipState.rows.length === 0) return null;

    const maxRows = 4;
    const displayedRows = tooltipState.rows.slice(0, maxRows);
    const hiddenCount = Math.max(
      0,
      tooltipState.rows.length - displayedRows.length
    );

    const hasXPx =
      typeof tooltipState.xPixel === "number" && isFinite(tooltipState.xPixel);
    const widthForMath = Math.max(
      1,
      Number(chartDimensions.width ?? resolvedWidth ?? 0)
    );
    const maxWidthPx = Math.max(240, Math.min(360, widthForMath - 40));
    const effectiveWidth = Math.min(tooltipWidth ?? maxWidthPx, maxWidthPx);
    const halfWidth = effectiveWidth / 2;
    const clampedX = hasXPx
      ? Math.min(
          Math.max(tooltipState.xPixel, halfWidth + 4),
          widthForMath - halfWidth - 4
        )
      : null;

    const leftStyle = hasXPx
      ? {
          left: clampedX,
          transform: [{ translateX: -halfWidth }],
          alignItems: "center",
        }
      : { left: 0, right: 0, alignItems: "center" };

    return (
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          right: hasXPx ? undefined : 0,
          ...leftStyle,
          bottom: 40,
          paddingHorizontal: 12,
          zIndex: 5,
        }}
      >
        <View
          style={{
            backgroundColor: Colors.whiteColor,
            borderRadius: 14,
            padding: 12,
            borderWidth: 1,
            borderColor: Colors.borderColor,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.12,
            shadowRadius: 12,
            elevation: 4,
            maxWidth: maxWidthPx,
          }}
          onLayout={(e) => {
            const w = e?.nativeEvent?.layout?.width;
            if (typeof w === "number" && isFinite(w)) {
              setTooltipWidth(w);
            }
          }}
        >
          <Text
            style={[
              Fonts.regular12,
              {
                color: Colors.grayColor,
                marginBottom: 8,
                textAlign: "center",
                fontWeight: "500",
              },
            ]}
          >
            {tooltipState.xLabel}
          </Text>

          {displayedRows.map((row, idx) => (
            <View
              key={row.key}
              style={{
                marginBottom: idx === displayedRows.length - 1 ? 0 : 12,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <View
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 7.5,
                    marginRight: 6,
                    backgroundColor: row.color ?? Colors.bubbleColor,
                  }}
                />
                <Text
                  style={[
                    Fonts.regular12,
                    {
                      color: Colors.blackColor,
                      fontWeight: "600",
                      flexShrink: 1,
                    },
                  ]}
                  numberOfLines={1}
                >
                  {row.label}
                </Text>
              </View>

              {row.metrics?.map((metric) => (
                <View
                  key={metric.key}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Text
                    style={[
                      Fonts.regular12,
                      {
                        color: Colors.grayColor,
                        minWidth: 90,
                        marginRight: 6,
                        textAlign: "left",
                      },
                    ]}
                    numberOfLines={1}
                  >
                    {metric.label}
                  </Text>
                  <Text
                    style={[
                      Fonts.regular12,
                      metric.value !== "-"
                        ? { color: Colors.darkgrayColor, fontWeight: "600" }
                        : { color: Colors.grayColor },
                    ]}
                  >
                    {metric.value}
                  </Text>
                </View>
              ))}
            </View>
          ))}

          {hiddenCount > 0 ? (
            <Text
              style={[
                Fonts.regular12,
                {
                  color: Colors.grayColor,
                  marginTop: 2,
                  marginBottom: -4,
                  textAlign: "center",
                  fontWeight: "400",
                  fontSize: 10,
                },
              ]}
            >
              {hiddenCount > 1
                ? t("tooltipHiddenEntries-string", { count: hiddenCount })
                : t("tooltipHiddenEntry-string", { count: hiddenCount })}
            </Text>
          ) : null}
        </View>
      </View>
    );
  };

  return (
    <ScrollView
      contentContainerStyle={{ flex: 1 }}
      horizontal
      showsHorizontalScrollIndicator={false}
    >
      <View
        style={{
          width: "100%",
          minWidth: 240,
          borderRadius: 14,
          overflow: "hidden",
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          backgroundColor: Colors.whiteColor,
          padding: CARD_PADDING,
        }}
      >
        <View
          style={hasNoSeries ? { opacity: 0.3 } : null}
          pointerEvents={hasNoSeries ? "none" : "auto"}
        >
          <View
            style={[
              GlobalStyles.HStack,
              {
                marginBottom: 8,
                alignItems: "flex-start",
                zIndex: 2,
              },
            ]}
          >
            <ScrollView
              style={{ maxWidth: "100%" }}
              contentContainerStyle={{
                flexDirection: "row",
                columnGap: 8,
                paddingRight: 8,
              }}
              horizontal
              showsHorizontalScrollIndicator={false}
            >
              <ChartTypePicker
                chartType="line"
                metricByType={metricByType}
                setMetricByType={setMetricByType}
                t={t}
                metricOptions={metricOptions}
              />
              <ChartTypePicker
                chartType="area"
                metricByType={metricByType}
                setMetricByType={setMetricByType}
                t={t}
                metricOptions={metricOptions}
              />
              {canShowBarResolved ? (
                <ChartTypePicker
                  chartType="bar"
                  metricByType={metricByType}
                  setMetricByType={setMetricByType}
                  t={t}
                  metricOptions={metricOptions}
                />
              ) : null}
            </ScrollView>
          </View>

          <View
            style={{
              height: 360,
              marginBottom: -12,
              marginTop: -20,
              zIndex: 0,
              position: "relative",
            }}
            onLayout={onChartLayout}
            onMouseMove={
              enableTooltipInteractions ? handleMouseMove : undefined
            }
            onMouseLeave={
              enableTooltipInteractions ? handleMouseLeave : undefined
            }
          >
            <VictoryChart
              width={resolvedWidth}
              height={resolvedHeight}
              padding={CHART_PADDING}
              domainPadding={{ y: [20, 20], x: [15, 15] }}
              scale={{ x: mode === "weeks" ? "linear" : "time" }}
              domain={xDomain}
              style={
                isMobile
                  ? {
                      parent: {
                        touchAction: "pan-y",
                        pointerEvents: "none",
                      },
                    }
                  : undefined
              }
              {...(chartContainer
                ? { containerComponent: chartContainer }
                : {})}
            >
              <VictoryAxis
                fixLabelOverlap
                tickFormat={xTickFormat}
                tickValues={weekTickValues}
                style={{
                  axis: { stroke: Colors.darkgrayColor, opacity: 0.6 },
                  grid: {
                    stroke: Colors.lightGrayColor,
                    strokeDasharray: "4",
                    opacity: 1,
                  },
                  tickLabels: {
                    fill: Colors.grayColor,
                    fontSize: 11,
                    fontWeight: "500",
                    fontFamily:
                      "system-ui, -apple-system, 'Segoe UI', sans-serif",
                  },
                }}
              />

              <VictoryAxis
                dependentAxis
                tickCount={10}
                // We normalize layers to share one axis (legacy behavior),
                // so raw values would be misleading here.
                tickFormat={() => ""}
                style={{
                  axis: { stroke: Colors.darkgrayColor, opacity: 0.6 },
                  grid: {
                    stroke: Colors.lightGrayColor,
                    strokeDasharray: "4",
                    opacity: 1,
                  },
                  tickLabels: { fill: Colors.grayColor, fontSize: 12 },
                }}
              />

              {/* All layers render together (legacy behavior). */}
              {renderBarLayer()}
              {renderAreaLayer()}
              {renderLineLayer()}
            </VictoryChart>

            <TooltipOverlay />
          </View>

          <Legend />
        </View>

        {hasNoSeries ? (
          <View
            style={{
              ...StyleSheet.absoluteFillObject,
              justifyContent: "center",
              alignItems: "center",
              zIndex: 5,
              paddingHorizontal: 16,
            }}
            // Keep horizontal scroll usable even when the overlay is shown.
            pointerEvents="none"
          >
            <View
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderRadius: 14,
                alignItems: "center",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
                borderWidth: 1,
                borderColor: Colors.borderColor,
                maxWidth: 260,
              }}
            >
              <Ionicons
                name="analytics-sharp"
                size={24}
                color={Colors.grayColor}
              />
              <Text
                style={[
                  Fonts.regular14,
                  {
                    fontSize: 13,
                    fontWeight: "600",
                    color: Colors.darkgrayColor,
                    marginTop: 8,
                    textAlign: "center",
                  },
                ]}
              >
                {t("noDataYet-string")}
              </Text>
              <Text
                style={[
                  Fonts.regular12,
                  {
                    fontSize: 11,
                    color: Colors.grayColor,
                    marginTop: 6,
                    textAlign: "center",
                  },
                ]}
              >
                {t("statisticsChartNoDataInfo-string")}
              </Text>
            </View>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
});

export default ClientStatisticsChart;
